import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import type { Balance } from '@src/types';
import { WarningSeverityIcon, type MobileCardRendererProps, type RenderCellOptions } from '@talos/kyoko';
import type { BalancesBlotterShowBy } from '../../../../Blotters/BalancesV2/types';
import { MobileBlotterCard } from '../MobileBlotterCardLayout';
import { useCompactMobileCardLayout } from '../useCompactMobileCardLayout';
import { getRequiredBalanceMetrics } from './mobileBalanceMetric';
import { getBalanceWarning } from './mobileBalanceWarnings';

export const MOBILE_BALANCE_CARD_HEIGHT = 104;
export const COMPACT_MOBILE_BALANCE_CARD_HEIGHT = 52;

export function useMobileBalanceCardLayout() {
  return useCompactMobileCardLayout(MOBILE_BALANCE_CARD_HEIGHT, COMPACT_MOBILE_BALANCE_CARD_HEIGHT);
}

const VISIBLE_COLUMN_RENDER_OPTIONS = { overrideColumnVisibility: true } satisfies RenderCellOptions;

interface MobileBalanceCardProps extends MobileCardRendererProps<Balance> {
  showBy: BalancesBlotterShowBy;
}

export function MobileBalanceCard({ data, renderCell, onPress, compact = false, showBy }: MobileBalanceCardProps) {
  const { homeCurrency } = useDisplaySettings();
  const marketAccount = renderCell('marketAccountName', VISIBLE_COLUMN_RENDER_OPTIONS);
  const instrument = renderCell('Currency', VISIBLE_COLUMN_RENDER_OPTIONS);
  const primaryCell = showBy === 'asset' ? marketAccount : instrument;
  const secondaryCell = showBy === 'counterparty' ? marketAccount : null;
  const showReconWarningColumn = renderCell('reconWarning') != null;
  const reconWarning = showReconWarningColumn ? getBalanceWarning(data) : undefined;
  const [quantityMetric, equivalentAmountMetric, availableAmountMetric] = getRequiredBalanceMetrics(
    renderCell,
    homeCurrency
  );

  return (
    <MobileBlotterCard
      compact={compact}
      onPress={onPress}
      testId="mobile-balance-card"
      instrumentTestId={showBy === 'asset' ? 'mobile-balance-card-market-account' : 'mobile-balance-card-instrument'}
      marketAccountTestId="mobile-balance-card-market-account"
      warningTestId="mobile-balance-card-recon-warning"
      instrument={primaryCell}
      marketAccount={secondaryCell}
      warning={
        reconWarning ? <WarningSeverityIcon severity={reconWarning.severity} data-testid="warning-bar" /> : undefined
      }
      primaryMetric={quantityMetric}
      compactMetric={equivalentAmountMetric}
      secondaryMetrics={[equivalentAmountMetric, availableAmountMetric]}
    />
  );
}
